import {
    tokenObserver
} from "../constants/observers.js";
import {
    createConditionScene
} from "./condition-scene.js";

/**
 * Initialises the token death/condition animations. Each animation adds a CSS
 * class to the token element, waits for the ending keyframe and cleans up,
 * while the matching frames are captured into the grimoire timeline so that
 * the animation is visible in the generated video, not just in the grimoire.
 * The scene is drawn procedurally on a canvas over a single pad capture, so
 * any number of frames is cheap.
 *
 * @param  {GrimoireTimeline} timeline
 *         The timeline that the animation frames are captured into.
 * @return {Function}
 *         The animation runner - call it with a token element and an animation
 *         kind ("execution", "demon", "poisoned" or "drunk") to run the
 *         animation manually.
 */
export function initTokenAnimations(timeline) {

    const TOKEN_ANIMATIONS = {
        execution: {
            className: "is-executed",
            endAnimation: "execution-fall",
            capture: (element) => timeline.captureExecution(element, { steps: 18 })
        },
        demon: {
            className: "is-demon-killed",
            endAnimation: "demon-kill-recoil",
            capture: (element) => timeline.captureDemonKill(element, { steps: 18 })
        },
        poisoned: {
            className: "is-being-poisoned",
            endAnimation: "condition-effect",
            capture: (element) => timeline.captureCondition(element, "poisoned")
        },
        drunk: {
            className: "is-becoming-drunk",
            endAnimation: "condition-effect",
            capture: (element) => timeline.captureCondition(element, "drunk")
        }
    };

    const tokenAnimationCleanups = new WeakMap();

    const runTokenAnimation = (element, kind) => {

        if (!element) {
            return;
        }

        tokenAnimationCleanups.get(element)?.();
        element.animationVersion = (element.animationVersion || 0) + 1;
        const { className, endAnimation, capture } = TOKEN_ANIMATIONS[kind];
        const cleanup = () => {
            element.classList.remove(className);
            element.removeEventListener("animationend", onAnimationEnd);
            tokenAnimationCleanups.delete(element);
        };
        const onAnimationEnd = ({ target, animationName }) => {

            if (target === element && animationName === endAnimation) {
                cleanup();
            }

        };

        tokenAnimationCleanups.set(element, cleanup);
        element.classList.add(className);
        element.addEventListener("animationend", onAnimationEnd);
        capture(element);

    };

    tokenObserver.on("character-executed", ({ detail }) => {
        runTokenAnimation(detail.element, "execution");
    });

    tokenObserver.on("character-killed-by-demon", ({ detail }) => {
        runTokenAnimation(detail.element, "demon");
    });

    tokenObserver.on("character-condition-applied", ({ detail }) => {
        const { element, kind } = detail;
        if (!element || !["poisoned", "drunk"].includes(kind)
            || element.classList.contains("is-executed") || element.classList.contains("is-demon-killed")) {
            return;
        }
        element.querySelectorAll(".condition-scene").forEach((scene) => scene.remove());
        element.classList.remove("is-being-poisoned", "is-becoming-drunk");
        element.append(createConditionScene(document, kind));
        runTokenAnimation(element, kind);
    });

    return runTokenAnimation;

}
